import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http, formatErr } from "../../lib/api";
import { downloadB64 } from "../../lib/files";
import { usePolling } from "../../lib/usePolling";
import { FilterBanner } from "../../components/FilterBanner";
import { toast } from "sonner";
import { CheckCircle2, Clock, Download, ExternalLink } from "lucide-react";

export default function AgentMaterials() {
  const [list, setList] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [sp, setSp] = useSearchParams();
  const onlyPending = sp.get("filter") === "pending";

  const load = async () => {
    try { const { data } = await http.get("/crm/me/materials"); setList(data); setBlocked(false); }
    catch (e) { if (e?.response?.status === 403) { setBlocked(true); setList([]); } else toast.error(formatErr(e)); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  usePolling(load, { interval: 10000, enabled: !blocked });

  const ack = async (m) => {
    try { await http.post(`/crm/me/materials/${m.assignment_id}/acknowledge`); await load(); window.dispatchEvent(new Event("glowbit:badges-refresh")); toast.success("Presa visione confermata."); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const dl = async (m) => {
    try {
      const { data } = await http.get(`/crm/me/materials/${m.assignment_id}/download`);
      if (data.link) { window.open(data.link, "_blank"); return; }
      if (data.data_b64) downloadB64(data.data_b64, data.filename || "materiale", data.content_type);
    } catch (e) { toast.error(formatErr(e)); }
  };

  if (blocked) return <div data-testid="agent-materials-blocked" className="max-w-2xl mx-auto card-clean p-6 text-center"><h1 className="font-serif text-2xl mb-2">Materiali</h1><p className="text-sm text-neutral-600">Disponibili dopo l'attivazione del tuo account.</p></div>;
  if (list === null) return <div className="p-6 text-sm text-neutral-500">Caricamento…</div>;

  const pending = list.filter((m) => !m.acknowledged).length;
  const shown = onlyPending ? list.filter((m) => !m.acknowledged) : list;

  return (
    <div data-testid="agent-materials-page" className="max-w-4xl mx-auto">
      <h1 className="font-serif text-3xl mb-1">Materiali di vendita</h1>
      <p className="text-sm text-neutral-500 mb-5">{list.length === 0 ? "Nessun materiale assegnato al momento." : pending > 0 ? `${pending} materiali richiedono la tua presa visione (obbligatoria).` : "Tutti i materiali assegnati sono stati presi in visione."}</p>
      <FilterBanner label={onlyPending ? "Da prendere in visione" : null} onClear={() => setSp({})} />

      <div className="space-y-3" data-testid="agent-materials-list">
        {shown.length === 0 && <div className="card-clean p-6 text-sm text-neutral-500">Nessun materiale{onlyPending ? " da prendere in visione" : " assegnato"}.</div>}
        {shown.map((m) => (
          <div key={m.assignment_id} className="card-clean p-4 flex items-center gap-4" data-testid={`agent-material-${m.assignment_id}`}>
            <div className="flex-1">
              <div className="font-semibold">{m.title} <span className="text-[11px] text-neutral-400">{[`v${m.version}`, m.category].filter(Boolean).join(" · ")}</span></div>
              {m.description && <div className="text-xs text-neutral-500 mt-0.5">{m.description}</div>}
            </div>
            {m.has_file && <button onClick={() => dl(m)} data-testid={`agent-material-dl-${m.assignment_id}`} className="px-3 py-1.5 rounded-md border text-xs flex items-center gap-1" style={{ borderColor: "var(--border-silver)" }}>{m.link ? <ExternalLink className="w-3 h-3" /> : <Download className="w-3 h-3" />} Apri</button>}
            {m.acknowledged ? (
              <span className="text-xs text-green-700 flex items-center gap-1" data-testid={`agent-material-done-${m.assignment_id}`}><CheckCircle2 className="w-4 h-4" /> Presa visione</span>
            ) : (
              <button onClick={() => ack(m)} data-testid={`agent-material-ack-${m.assignment_id}`} className="px-3 py-1.5 rounded-md text-white text-xs flex items-center gap-1" style={{ background: "var(--brand-dark)" }}><Clock className="w-3 h-3" /> Conferma presa visione</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
